import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ArticleDetail, ArticleSummary } from '@/types'

import RichText from '@/components/cms/rich-text'
import { motion } from 'motion/react'
import { enter } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/motion/reveal'
import { SplitText } from '@/components/motion/split-text'
import { ArrowLink } from '@/components/site/arrow-link'
import { JournalCard } from '@/components/site/journal-card'
import { formatDate } from '@/lib/format'

import {
    getArticleBySlug,
    getArticleSlugs,
    getArticles
} from '@/lib/content'

interface ArticlePageProps {
    article: ArticleDetail
    related: ArticleSummary[]
}

interface CardGroupProps {
    articles: ArticleSummary[]
}

const CardGroup: FC<CardGroupProps> = ({ articles }): ReactNode => {
    if (articles.length === 0) return null

    return (
        <div className='grid w-full grid-cols-2 gap-10 lg:flex-1'>
            {articles.map((item, index) => (
                <Reveal key={item.id} className='w-full' delay={index * 0.08}>
                    <JournalCard article={item} />
                </Reveal>
            ))}
        </div>
    )
}

const ArticlePage: FC<ArticlePageProps> = ({ article, related }): ReactNode => {
    const firstGroup = related.slice(0, 2)
    const secondGroup = related.slice(2, 4)

    return (
        <Layout>
            <Seo
                title={article.title}
                description={article.excerpt ?? "Writing from Zaki Hatta, a software engineer from Padang, Indonesia."}
                ogImage={article.cover ?? undefined}
            />

            <article
                id='article'
                className='relative z-[2] flex w-full flex-col items-center bg-background px-4 py-section-sm lg:px-6'
            >
                <div className='flex w-full justify-center p-12 md:p-16'>
                    <ArrowLink href='/journal' label="Back to journal" direction='left' />
                </div>

                <div className='flex w-full max-w-[1200px] flex-col items-center gap-12 md:gap-16 lg:gap-20'>
                    <header className='flex w-full flex-col items-center gap-10 text-foreground md:max-w-[68%]'>
                        <div className='w-full overflow-clip'>
                            <motion.h1
                                data-mdlfy-cms={`articles:${article.id}:title`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={enter(0.1)}
                                className='t-name w-full text-left [text-wrap:balance]'
                            >
                                {article.title}
                            </motion.h1>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={enter(0.2)}
                            className='flex flex-wrap items-center justify-center gap-2'
                        >
                            {article.author && (
                                <>
                                    <span className='t-italic whitespace-nowrap text-umber'>
                                        Written by
                                    </span>

                                    <span
                                        data-mdlfy-cms={`articles:${article.id}:author`}
                                        className='t-body whitespace-nowrap'
                                    >
                                        {article.author}
                                    </span>
                                </>
                            )}

                            <span className='t-italic whitespace-nowrap text-umber'>
                                on
                            </span>

                            <time dateTime={article.publishedOn} className='t-body whitespace-nowrap'>
                                {formatDate(article.publishedOn)}
                            </time>
                        </motion.p>
                    </header>

                    {article.cover && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={enter(0.3)}
                            className='relative z-[1] aspect-[1.6] w-full overflow-hidden bg-muted lg:aspect-[1.547]'
                        >
                            <img
                                src={article.cover}
                                alt={article.coverAlt ?? ''}
                                decoding='async'
                                className='absolute inset-0 size-full object-cover'
                            />
                        </motion.div>
                    )}

                    {article.excerpt && (
                        <p
                            data-mdlfy-cms={`articles:${article.id}:excerpt`}
                            className='t-body w-full text-left text-foreground md:max-w-[68%]'
                        >
                            {article.excerpt}
                        </p>
                    )}

                    {article.body && (
                        <RichText html={article.body} className='w-full md:max-w-[68%]' />
                    )}
                </div>
            </article>

            {related.length > 0 && (
                <section
                    id='more-like-this'
                    className='relative z-[2] flex w-full flex-col items-center gap-12 overflow-clip bg-background px-4 pb-section-lg pt-20 md:gap-16 lg:flex-row lg:items-start lg:gap-20 lg:px-6'
                >
                    <div className='order-2 w-full lg:order-1 lg:flex-1'>
                        <CardGroup articles={firstGroup} />
                    </div>

                    <h2
                        aria-label="More like this"
                        className='order-1 flex w-min flex-col items-start self-center py-6 text-foreground lg:order-2 lg:self-stretch'
                    >
                        <SplitText
                            text="More"
                            effect='slide'
                            accessible={false}
                            className='t-name block whitespace-pre'
                        />

                        {' '}

                        <span className='flex items-center gap-4'>
                            <SplitText
                                text="like"
                                effect='fade'
                                accessible={false}
                                className='t-number block whitespace-pre'
                            />

                            {' '}

                            <SplitText
                                text="this"
                                effect='slide'
                                delay={0.1}
                                accessible={false}
                                className='t-name block whitespace-pre'
                            />
                        </span>
                    </h2>

                    <div className='order-3 w-full lg:flex-1'>
                        <CardGroup articles={secondGroup} />
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getArticleSlugs()

        return { paths: slugs.map((slug) => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Article paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ArticlePageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    try {
        const [article, articles] = await Promise.all([getArticleBySlug(slug), getArticles()])

        if (!article) return { notFound: true, revalidate: 60 }

        const sameTopic = articles.filter((item) => item.slug !== slug && article.topic && item.topic?.id === article.topic.id)
        const others = articles.filter((item) => item.slug !== slug && !sameTopic.includes(item))
        const related = [...sameTopic, ...others].slice(0, 4)

        return { props: { article, related }, revalidate: 60 }
    }
    catch (error) {
        console.error('Article could not be loaded:', error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 10 }
    }
}

export default ArticlePage
